import { API, Auth, graphqlOperation } from "aws-amplify";
import { useEffect, useRef, useState } from "react";
import aiSpinner from "../../../lottie/Animation - 1709894762114.json";
import { csvString } from "../../../data/cpvGroep2022data";
import { readString } from "react-papaparse";
import { API_DEFAULT_HEADERS, API_DOMAIN } from "../../../APIconfig";
import Lottie from "lottie-react";

interface CpvSelection {
    cpvcode: string;
    omschrijving: string;
}

interface Props {
    onClose: () => void;
    onNext: () => void;
    keywords: string[];
    selection: any[];
    setSelection: (selection: any) => void;
}

interface CatalogueEntry {
    CODE: string;
    Omschrijving: string;
}

interface SummaryRow {
    _key?: string;
    value?: string;
    emails?: string;
}

// Accept the direct response, a Lambda envelope, and the old double encoding.
function parseCpvResponse(payload: unknown): string[] {
    let value: any = payload;
    for (let depth = 0; depth < 4; depth += 1) {
        if (typeof value === "string") {
            value = JSON.parse(value);
        } else if (value && typeof value === "object" && "body" in value) {
            if (Number(value.statusCode) >= 400) {
                throw new Error("De CPV-service heeft een fout geretourneerd.");
            }
            value = value.body;
        } else {
            break;
        }
    }
    if (!Array.isArray(value?.cpvcodes) ||
        !value.cpvcodes.every((code: unknown) => typeof code === "string")) {
        throw new Error("De CPV-service gaf een ongeldig antwoord.");
    }
    return value.cpvcodes;
}

// Hard cap: a single common keyword (e.g. "diensten") substring-matches 1000+ of the
// 9455 catalogue entries, which previously sent the cpvlink Lambda into a multi-minute
// loop and tripped API Gateway's non-configurable 29s timeout (504).
const MAX_SELECTED_CPVS = 30;

function selectCpvs(codes: string[], keywords: string[], catalogue: CatalogueEntry[]) {
    const byCode = new Map(catalogue.map((entry) => [String(entry.CODE).split("-")[0], entry]));
    const selected = new Map<string, CpvSelection>();
    const add = (entry: CatalogueEntry) => {
        if (selected.size >= MAX_SELECTED_CPVS) return;
        const cpvcode = String(entry.CODE).split("-")[0];
        selected.set(cpvcode, { cpvcode, omschrijving: entry.Omschrijving });
    };
    for (const code of codes) {
        const entry = byCode.get(code.trim().split("-")[0]);
        if (entry) add(entry);
    }
    // Preserve the existing local keyword fallback, without async array callbacks.
    const blacklist = new Set(["van", "de", "het", "bouw", "bouwen", "software"]);
    const words = new Set(keywords.flatMap((text) => text.toLowerCase().trim().split(/\s+/))
        .filter((word) => word && !blacklist.has(word)));
    for (const entry of catalogue) {
        if (selected.size >= MAX_SELECTED_CPVS) break;
        const description = String(entry.Omschrijving).toLowerCase();
        if (Array.from(words).some((word) => description.includes(word))) add(entry);
    }
    return Array.from(selected.values());
}

function calculateSummary(rows: SummaryRow[], selected: CpvSelection[]) {
    const groups = new Set(selected.map((entry) => entry.cpvcode.substring(0, 3)));
    return rows.reduce((totals, row) => {
        if (groups.has(String(row._key))) {
            const value = Number(row.value);
            const emails = Number(row.emails);
            if (Number.isFinite(value)) totals.value += value;
            if (Number.isFinite(emails)) totals.emails += emails;
        }
        return totals;
    }, { value: 0, emails: 0 });
}

function waitForSummary(signal: AbortSignal) {
    return new Promise<void>((resolve, reject) => {
        const cancel = () => {
            clearTimeout(timer);
            signal.removeEventListener("abort", cancel);
            reject(new Error("Cancelled"));
        };
        const timer = setTimeout(() => {
            signal.removeEventListener("abort", cancel);
            resolve();
        }, 5000);
        signal.addEventListener("abort", cancel, { once: true });
        if (signal.aborted) cancel();
    });
}

export default function ResultsOnboarding({ onClose, onNext, keywords, setSelection }: Props) {
    const [loadingPhase, setLoadingPhase] = useState(1);
    const [summaryItemsValue, setSummaryItemsValue] = useState(0);
    const [summaryItemsEmails, setSummaryItemsEmails] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const callbacks = useRef({ onNext, setSelection });
    useEffect(() => { callbacks.current = { onNext, setSelection }; }, [onNext, setSelection]);
    const keywordsKey = JSON.stringify(keywords);

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;
        const activeKeywords: string[] = JSON.parse(keywordsKey);
        setError(null);
        setLoadingPhase(1);

        async function run() {
            try {
                if (activeKeywords.length !== 5 || activeKeywords.some((word) =>
                    typeof word !== "string" || !word.trim() || word.length > 200)) {
                    throw new Error("Vul vijf trefwoorden in (maximaal 200 tekens per trefwoord).");
                }
                const cpvCodes = require("../../../data/cpvCodes.json");
                const catalogue: CatalogueEntry[] = cpvCodes["CPV codes"];
                if (!Array.isArray(catalogue)) throw new Error("De CPV-catalogus kon niet worden geladen.");
                const rows = await new Promise<SummaryRow[]>((resolve, reject) => {
                    readString(csvString, {
                        worker: true,
                        header: true,
                        skipEmptyLines: true,
                        complete: (result) => {
                            if (result.errors.length) reject(new Error("De samenvattingsgegevens zijn ongeldig."));
                            else resolve(result.data as SummaryRow[]);
                        },
                    });
                });
                if (signal.aborted) return;
                const session = await Auth.currentSession();
                if (signal.aborted) return;
                const headers = new Headers(API_DEFAULT_HEADERS(session.getIdToken().getJwtToken()));
                headers.set("Content-Type", "application/json");
                const response = await fetch(`${API_DOMAIN}/ai/cpvgenerator`, {
                    method: "POST", headers, signal,
                    body: JSON.stringify({ keywords: activeKeywords }),
                });
                if (!response.ok) throw new Error(`CPV-aanvraag mislukt (${response.status}).`);
                const selected = selectCpvs(parseCpvResponse(await response.json()), activeKeywords, catalogue);
                if (signal.aborted) return;
                if (!selected.length) throw new Error("Geen geldige CPV-codes gevonden. Pas je trefwoorden aan en probeer opnieuw.");
                callbacks.current.setSelection(selected);
                const totals = calculateSummary(rows, selected);
                setSummaryItemsValue(totals.value);
                setSummaryItemsEmails(totals.emails);
                setLoadingPhase(2);
                await waitForSummary(signal);

                const { attributes } = await Auth.currentAuthenticatedUser();
                if (signal.aborted) return;
                const query = `query OnboardingCompany($id: ID!) {
                    getUser(id: $id) { companies { items { company { id } } } }
                }`;
                const userInfo: any = await API.graphql(graphqlOperation(query, { id: attributes.sub }));
                if (signal.aborted) return;
                if (userInfo.errors?.length) throw new Error("Je bedrijfsgegevens konden niet worden geladen.");
                const companyID = userInfo.data?.getUser?.companies?.items?.[0]?.company?.id;
                if (!companyID) throw new Error("Geen bedrijf gevonden voor dit account.");
                const freshSession = await Auth.currentSession();
                if (signal.aborted) return;
                const linkHeaders = new Headers(API_DEFAULT_HEADERS(freshSession.getIdToken().getJwtToken()));
                linkHeaders.set("Content-Type", "application/json");
                const linked = await fetch(`${API_DOMAIN}/user/cpvlink`, {
                    method: "POST", headers: linkHeaders, signal,
                    body: JSON.stringify({ userID: attributes.sub, companyID, cpvs: selected }),
                });
                if (!linked.ok) throw new Error(`CPV-codes koppelen mislukt (${linked.status}).`);
                // Some non-proxy integrations wrap a failing Lambda status in HTTP 200.
                const linkText = await linked.text();
                if (linkText.trim()) {
                    let linkResult: any;
                    try { linkResult = JSON.parse(linkText); } catch { /* Endpoint may return plain text. */ }
                    if (Number(linkResult?.statusCode) >= 400 || linkResult?.error || linkResult?.success === false) {
                        throw new Error("De CPV-codes konden niet aan je account worden gekoppeld.");
                    }
                }
                if (!signal.aborted) callbacks.current.onNext();
            } catch (cause) {
                if (signal.aborted) return;
                console.error("CPV onboarding failed", cause);
                setError(cause instanceof Error ? cause.message : "Er is iets misgegaan. Probeer opnieuw.");
            }
        }
        void run();
        return () => controller.abort();
    }, [keywordsKey, attempt]);

    return (
        <div className="p-10 flex flex-col items-center justify-center">
            {error ? (
                <div role="alert" className="text-center">
                    <p className="pb-5">{error}</p>
                    <button type="button" className="px-4 py-2" onClick={() => setAttempt((value) => value + 1)}>
                        Opnieuw proberen
                    </button>
                    <button type="button" className="px-4 py-2" onClick={onClose}>Sluiten</button>
                </div>
            ) : (
                <>
                    <Lottie animationData={aiSpinner} className="w-80" />
                    {loadingPhase === 1 && (
                        <>
                            <p className="pt-5">We zetten een voorspelling in elkaar</p>
                            <p className="pb-10 text-primary-pink">Dit kan even duren...</p>
                        </>
                    )}
                    {loadingPhase === 2 && (
                        <div className="group relative flex flex-col items-center w-full h-full mt-5">
                            <p className="pb-3">Aan de hand van jouw trefwoorden verwachten we de volgende <b>potentiële business</b></p>
                            <p className="text-4xl font-bold">{summaryItemsEmails.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                            <p className="pb-5">aanbestedingen per jaar</p>
                            <p className="pt-5 border-t">met een waarde van</p>
                            <p className="text-4xl font-bold">€{summaryItemsValue.toLocaleString(undefined, { maximumFractionDigits: 2 })},-</p>
                            <p className="pb-10 mt-5 text-primary-pink">We koppelen de voorspelling aan je account...</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
